import {configureStore} from "@reduxjs/toolkit";
import {chatUISlice} from "@/store/features/chat/chatUiSlice";
import UploadingFileSlice from "@/store/features/file/UploadingFileSlice";
import pendingMessageSlice from "@/store/features/chat/pendingMessageSlice";
import {themeSlice} from "@/store/features/theme/themeSlice";
import {chatApi} from "@/store/features/chat/chatApiSlice";
import {projectApiSlice} from "@/store/features/project/projectApiSlice";
import {themeApiSlice} from "@/store/features/theme/themeApiSlice";
import {projectFiles} from "@/store/features/file/fileApiSlice";
import {widgetApi, widgetBoApi} from "@/store/features/widget/widgetApiSlice";
import {scraperApi} from "@/store/features/scrapper/scraperApiSlice";
import {agentsApiSlice} from "@/store/features/agents/agentsApiSlice";
import {agentCreationApiSlice} from "@/store/features/agentCreation/agentCreationApiSlice";
import {collectionsApiSlice} from "@/store/features/collections/collectionsApiSlice";
import {llmModelsApiSlice} from "@/store/features/llmModels/llmModelsApiSlice";
import {toolsApiSlice} from "@/store/features/tools/toolsApiSlice";
import {toolExecutionApiSlice} from "@/store/features/tools/toolExecutionApiSlice";
import {tagsApiSlice} from "@/store/features/tags/tagsApiSlice";
import {usersApiSlice} from "@/store/features/user/userApiSlice";
import {n8nApi} from "@/store/features/n8nAutomations/n8nApiSlice";
import {iframableWidgetApiSlice} from "@/store/features/inframableWidget/iframableWidgetApiSlice";
import {integrationsApi} from "@/store/features/oauth/oauthApiSlice";
import {agentAccessApiSlice} from "@/store/features/agents/agentAccessApiSlice";
import {dynamicCollectionsApiSlice} from "@/store/features/dynamicCollections/dynamicCollectionsApiSlice";
import widgetBoDashboardApi from "@/store/features/widgetBo/widgetBoDashboardApiSlice";
import {channelsApiSlice} from "@/store/features/channel/channelApiSlice";
import {externalAgentApiSlice} from "@/store/features/externalAgent/externalAgentApiSlice";
import {adminToolsApiSlice} from "@/store/features/adminTools/adminToolsApiSlice";
import devRoleSlice from './features/role/devRoleApiSlice';
import {dashboardSlice} from './features/dashboard/dashboardSlice';
import {dashboardApi} from './features/dashboard/dashboardApi';
import { setStoreGetter } from '@/core/utils';

export const makeStore = () => {
    const store = configureStore({
        devTools: {
            trace: true,
        },
        reducer: {
            chatUI: chatUISlice.reducer,
            uploadingFiles: UploadingFileSlice.reducer,
            pendingMessage: pendingMessageSlice.reducer,
            theme: themeSlice.reducer,
            devRole: devRoleSlice.reducer,
            dashboard: dashboardSlice.reducer,
            [chatApi.reducerPath]: chatApi.reducer,
            [projectApiSlice.reducerPath]: projectApiSlice.reducer,
            [themeApiSlice.reducerPath]: themeApiSlice.reducer,
            [projectFiles.reducerPath]: projectFiles.reducer,
            [widgetApi.reducerPath]: widgetApi.reducer,
            [widgetBoApi.reducerPath]: widgetBoApi.reducer,
            scraperApi: scraperApi.reducer,
            [agentsApiSlice.reducerPath]: agentsApiSlice.reducer,
            [agentCreationApiSlice.reducerPath]: agentCreationApiSlice.reducer,
            [collectionsApiSlice.reducerPath]: collectionsApiSlice.reducer,
            [llmModelsApiSlice.reducerPath]: llmModelsApiSlice.reducer,
            [toolsApiSlice.reducerPath]: toolsApiSlice.reducer,
            [toolExecutionApiSlice.reducerPath]: toolExecutionApiSlice.reducer,
            [tagsApiSlice.reducerPath]: tagsApiSlice.reducer,
            [integrationsApi.reducerPath]: integrationsApi.reducer,
            [usersApiSlice.reducerPath]: usersApiSlice.reducer,
            [n8nApi.reducerPath]: n8nApi.reducer,
            [iframableWidgetApiSlice.reducerPath]: iframableWidgetApiSlice.reducer,
            [agentAccessApiSlice.reducerPath]: agentAccessApiSlice.reducer,
            [dynamicCollectionsApiSlice.reducerPath]: dynamicCollectionsApiSlice.reducer,
            [widgetBoDashboardApi.reducerPath]: widgetBoDashboardApi.reducer,
            [channelsApiSlice.reducerPath]: channelsApiSlice.reducer,
            [externalAgentApiSlice.reducerPath]: externalAgentApiSlice.reducer,
            [adminToolsApiSlice.reducerPath]: adminToolsApiSlice.reducer,
            [dashboardApi.reducerPath]: dashboardApi.reducer,
        },
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({
                serializableCheck: false
            })
                .concat(chatApi.middleware)
                .concat(projectFiles.middleware)
                .concat(projectApiSlice.middleware)
                .concat(themeApiSlice.middleware)
                .concat(widgetApi.middleware)
                .concat(widgetBoApi.middleware)
                .concat(scraperApi.middleware)
                .concat(agentsApiSlice.middleware)
                .concat(agentCreationApiSlice.middleware)
                .concat(collectionsApiSlice.middleware)
                .concat(llmModelsApiSlice.middleware)
                .concat(toolsApiSlice.middleware)
                .concat(toolExecutionApiSlice.middleware)
                .concat(tagsApiSlice.middleware)
                .concat(integrationsApi.middleware)
                .concat(usersApiSlice.middleware)
                .concat(n8nApi.middleware)
                .concat(iframableWidgetApiSlice.middleware)
                .concat(agentAccessApiSlice.middleware)
                .concat(dynamicCollectionsApiSlice.middleware)
                .concat(widgetBoDashboardApi.middleware)
                .concat(channelsApiSlice.middleware)
                .concat(externalAgentApiSlice.middleware)
                .concat(adminToolsApiSlice.middleware)
                .concat(dashboardApi.middleware)

    });
    setStoreGetter(() => store.getState());

    return store;
};

export type AppStore = ReturnType<typeof makeStore>;
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];